import React, { useState, useEffect } from "react";
import "./createVendorDialog.css";
import {
  Building2, User, Phone, Mail, MapPin, Hash,
  CreditCard, FileText, X, Tag, Globe,
} from "lucide-react";

const VENDOR_TYPES = [
  "Online Portal", "Hotel", "DMC", "Transporter", "Airlines",
  "Visa Service Provider", "Travel Insurance", "Cruise", "Sightseeing", "Other",
];

const EMPTY = {
  vendorType: "",
  vendorLegalName: "",
  contactPersonName: "",
  designation: "",
  contactNumber: "",
  secondaryContactNumber: "",
  whatsappNumber: "",
  email: "",
  address: {
    addressLine1: "", addressLine2: "", city: "",
    stateProvince: "", pinCode: "", country: "India",
  },
  serviceLocations: "",
  businessType: "",
  gstNumber: "",
  panNumber: "",
  msmeRegistrationNumber: "",
  bankName: "",
  branchName: "",
  beneficiaryName: "",
  accountNumber: "",
  accountType: "",
  ifscSwiftIban: "",
  isActive: true,
};

/* ─────────────────────────────────────────────────────────────────────────────
   Field wrapper defined OUTSIDE the main component.
   This is the root-cause fix: if defined inside, React treats it as a brand-new
   component type on every render, unmounts + remounts the DOM node, and the
   focused input loses focus after every keystroke.
───────────────────────────────────────────────────────────────────────────── */
const Field = ({ label, err, children, fullWidth }) => (
  <div
    className={[
      "vendor-form-field",
      fullWidth ? "vendor-full-width" : "",
      err       ? "vendor-field-error" : "",
    ].join(" ").trim()}
  >
    <label>{label}</label>
    {children}
    {err && <span className="vendor-error-text">{err}</span>}
  </div>
);

export default function CreateVendorDialog({
  open, onClose, onSubmit, initialData = null, isEdit = false, isLoading = false,
}) {
  const [form, setForm]     = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          ...EMPTY,
          ...initialData,
          address: { ...EMPTY.address, ...(initialData.address || {}) },
          serviceLocations: Array.isArray(initialData.serviceLocations)
            ? initialData.serviceLocations.join(", ")
            : (initialData.serviceLocations || ""),
        });
      } else {
        setForm(EMPTY);
      }
      setErrors({});
    }
  }, [open, initialData]);

  if (!open) return null;

  const set     = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setAddr = (key, val) => setForm((f) => ({ ...f, address: { ...f.address, [key]: val } }));

  const validate = () => {
    const e = {};
    if (!form.vendorType)               e.vendorType         = "Vendor type is required";
    if (!form.vendorLegalName.trim())   e.vendorLegalName    = "Legal name is required";
    if (!form.contactPersonName.trim()) e.contactPersonName  = "Contact person is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = {
      ...form,
      serviceLocations: form.serviceLocations
        ? form.serviceLocations.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
    try { await onSubmit(payload); }
    catch { /* handled in parent */ }
  };

  return (
    <div className="vendor-dialog-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="vendor-dialog-container">

        {/* ── Header ── */}
        <div className="vendor-dialog-header">
          <h2>{isEdit ? "✏️ Edit Vendor" : "➕ Add New Vendor"}</h2>
          <button className="vendor-close-btn" onClick={onClose} type="button">
            <X size={20} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="vendor-dialog-body">
          {isEdit && initialData?.vendorId && (
            <div className="vendor-id-badge">
              <Tag size={14} /> Vendor ID: {initialData.vendorId}
            </div>
          )}

          <form onSubmit={handleSubmit}>

            {/* 1. Basic Details */}
            <div className="vendor-form-section">
              <h3><Building2 size={14} /> Basic Details</h3>
              <div className="vendor-form-grid">

                <Field label="Vendor Type *" err={errors.vendorType}>
                  <select value={form.vendorType} onChange={(e) => set("vendorType", e.target.value)}>
                    <option value="">— Select Type —</option>
                    {VENDOR_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>

                <Field label="Vendor Legal Name *" err={errors.vendorLegalName} fullWidth>
                  <div className="vendor-input-icon">
                    <Building2 size={15} />
                    <input
                      type="text"
                      value={form.vendorLegalName}
                      onChange={(e) => set("vendorLegalName", e.target.value)}
                      placeholder="Registered legal name of vendor"
                    />
                  </div>
                </Field>

              </div>
            </div>

            {/* 2. Contact Details */}
            <div className="vendor-form-section">
              <h3><User size={14} /> Contact Details</h3>
              <div className="vendor-form-grid">

                <Field label="Contact Person Name *" err={errors.contactPersonName}>
                  <div className="vendor-input-icon">
                    <User size={15} />
                    <input
                      type="text"
                      value={form.contactPersonName}
                      onChange={(e) => set("contactPersonName", e.target.value)}
                      placeholder="Full name"
                    />
                  </div>
                </Field>

                <Field label="Designation">
                  <div className="vendor-input-icon">
                    <FileText size={15} />
                    <input
                      type="text"
                      value={form.designation}
                      onChange={(e) => set("designation", e.target.value)}
                      placeholder="e.g. Manager, Director"
                    />
                  </div>
                </Field>

                <Field label="Contact Number">
                  <div className="vendor-input-icon">
                    <Phone size={15} />
                    <input
                      type="tel"
                      value={form.contactNumber}
                      onChange={(e) => set("contactNumber", e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </Field>

                <Field label="Secondary Contact Number">
                  <div className="vendor-input-icon">
                    <Phone size={15} />
                    <input
                      type="tel"
                      value={form.secondaryContactNumber}
                      onChange={(e) => set("secondaryContactNumber", e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </Field>

                <Field label="WhatsApp Number (if different)">
                  <div className="vendor-input-icon">
                    <Phone size={15} />
                    <input
                      type="tel"
                      value={form.whatsappNumber}
                      onChange={(e) => set("whatsappNumber", e.target.value)}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </Field>

                <Field label="Email ID">
                  <div className="vendor-input-icon">
                    <Mail size={15} />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="vendor@example.com"
                    />
                  </div>
                </Field>

                <Field label="Address Line 1" fullWidth>
                  <div className="vendor-input-icon">
                    <MapPin size={15} />
                    <input
                      type="text"
                      value={form.address.addressLine1}
                      onChange={(e) => setAddr("addressLine1", e.target.value)}
                      placeholder="Street / Building"
                    />
                  </div>
                </Field>

                <Field label="Address Line 2" fullWidth>
                  <input
                    type="text"
                    value={form.address.addressLine2}
                    onChange={(e) => setAddr("addressLine2", e.target.value)}
                    placeholder="Area / Locality"
                  />
                </Field>

                <Field label="City">
                  <input
                    type="text"
                    value={form.address.city}
                    onChange={(e) => setAddr("city", e.target.value)}
                    placeholder="City"
                  />
                </Field>

                <Field label="State / Province">
                  <input
                    type="text"
                    value={form.address.stateProvince}
                    onChange={(e) => setAddr("stateProvince", e.target.value)}
                    placeholder="State"
                  />
                </Field>

                <Field label="Pin Code">
                  <input
                    type="text"
                    value={form.address.pinCode}
                    onChange={(e) => setAddr("pinCode", e.target.value)}
                    placeholder="Pin / ZIP"
                  />
                </Field>

                <Field label="Country">
                  <input
                    type="text"
                    value={form.address.country}
                    onChange={(e) => setAddr("country", e.target.value)}
                    placeholder="Country"
                  />
                </Field>

                <Field label="Service Locations / Destinations Covered" fullWidth>
                  <div className="vendor-input-icon">
                    <Globe size={15} />
                    <input
                      type="text"
                      value={form.serviceLocations}
                      onChange={(e) => set("serviceLocations", e.target.value)}
                      placeholder="e.g. Dubai, Maldives, Thailand (comma separated)"
                    />
                  </div>
                </Field>

              </div>
            </div>

            {/* 3. Legal Compliance */}
            <div className="vendor-form-section">
              <h3><FileText size={14} /> Legal Compliance</h3>
              <div className="vendor-form-grid">

                <Field label="Business Type">
                  <select value={form.businessType} onChange={(e) => set("businessType", e.target.value)}>
                    <option value="">— Select —</option>
                    {["Proprietor", "Partnership", "Pvt Ltd", "LLP", "Other"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="GST / TAX Registration Number">
                  <div className="vendor-input-icon">
                    <Hash size={15} />
                    <input
                      type="text"
                      value={form.gstNumber}
                      onChange={(e) => set("gstNumber", e.target.value)}
                      placeholder="GST / TAX No."
                    />
                  </div>
                </Field>

                <Field label="PAN Number">
                  <div className="vendor-input-icon">
                    <Hash size={15} />
                    <input
                      type="text"
                      value={form.panNumber}
                      onChange={(e) => set("panNumber", e.target.value)}
                      placeholder="PAN Number"
                    />
                  </div>
                </Field>

                <Field label="MSME / SME Registration No. (if applicable)">
                  <div className="vendor-input-icon">
                    <Hash size={15} />
                    <input
                      type="text"
                      value={form.msmeRegistrationNumber}
                      onChange={(e) => set("msmeRegistrationNumber", e.target.value)}
                      placeholder="MSME / SME No."
                    />
                  </div>
                </Field>

              </div>
            </div>

            {/* 4. Payment Details */}
            <div className="vendor-form-section">
              <h3><CreditCard size={14} /> Payment Details</h3>
              <div className="vendor-form-grid">

                <Field label="Bank Name">
                  <div className="vendor-input-icon">
                    <CreditCard size={15} />
                    <input
                      type="text"
                      value={form.bankName}
                      onChange={(e) => set("bankName", e.target.value)}
                      placeholder="Bank name"
                    />
                  </div>
                </Field>

                <Field label="Branch Name">
                  <input
                    type="text"
                    value={form.branchName}
                    onChange={(e) => set("branchName", e.target.value)}
                    placeholder="Branch name"
                  />
                </Field>

                <Field label="Beneficiary Name">
                  <div className="vendor-input-icon">
                    <User size={15} />
                    <input
                      type="text"
                      value={form.beneficiaryName}
                      onChange={(e) => set("beneficiaryName", e.target.value)}
                      placeholder="Account holder name"
                    />
                  </div>
                </Field>

                <Field label="Account Number">
                  <input
                    type="text"
                    value={form.accountNumber}
                    onChange={(e) => set("accountNumber", e.target.value)}
                    placeholder="Account number"
                  />
                </Field>

                <Field label="Account Type">
                  <select value={form.accountType} onChange={(e) => set("accountType", e.target.value)}>
                    <option value="">— Select —</option>
                    {["Savings", "Current", "Other"].map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>

                <Field label="IFSC / SWIFT / IBAN Number">
                  <div className="vendor-input-icon">
                    <Hash size={15} />
                    <input
                      type="text"
                      value={form.ifscSwiftIban}
                      onChange={(e) => set("ifscSwiftIban", e.target.value)}
                      placeholder="IFSC / SWIFT / IBAN"
                    />
                  </div>
                </Field>

              </div>
            </div>

            {/* 5. Status */}
            <div className="vendor-form-section">
              <h3>Status</h3>
              <div className="vendor-status-toggle">
                <label className="vendor-toggle-label">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => set("isActive", e.target.checked)}
                  />
                  <span className={`vendor-toggle-slider${form.isActive ? " active" : ""}`} />
                  <span className="vendor-toggle-text">
                    {form.isActive ? "Active" : "Inactive"}
                  </span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div className="vendor-dialog-actions">
              <button type="button" className="vendor-btn-outline" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="submit" className="vendor-btn-primary" disabled={isLoading}>
                {isLoading ? "Saving…" : isEdit ? "Update Vendor" : "Create Vendor"}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}